import { useState } from "react";
import { Download, X } from "lucide-react";
import api from "../../../../../../utils/axiosInstance";
import UpdateLinkedFilesModal from "./UpdateLinkedFilesModal";
import { toast } from "../../../../../../components/Toast/toast";

const LinkedInputFilesCard = ({ jobId, files = [], onRefresh }) => {
  const [showModal, setShowModal] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDownloadSingle = async (file) => {
    try {
      const filePath = file.is_linked
        ? file.linkedProjectFile?.file_path
        : file.file_path;

      if (!filePath) {
        toast.error("File path not found");
        return;
      }

      const response = await api.get(
        `/download-file?path=${encodeURIComponent(filePath)}`,
        { responseType: "blob" }
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_file_name || file.linkedProjectFile?.original_file_name || "file";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    }
  };

  const handleDownloadZip = async () => {
    if (files.length === 0) {
      toast.error("No files to download");
      return;
    }

    try {
      setDownloading(true);
      const response = await api.get(
        `/job-input-files/download-zip?job_id=${jobId}`,
        { responseType: "blob" }
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `job-${jobId}-input-files.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download zip error:", error);
      toast.error("Failed to download files as zip");
    } finally {
      setDownloading(false);
    }
  };

  const getFileName = (file) => {
    if (file.is_linked) {
      return file.linkedProjectFile?.original_file_name || "—";
    }
    return file.original_file_name || "—";
  };

  const getFileSize = (file) => {
    if (file.is_linked) {
      return formatFileSize(file.linkedProjectFile?.file_size);
    }
    return formatFileSize(file.file_size);
  };

  const HEADERS = ["File #","Filename","Size","Category","Note","Uploaded at","Uploaded by","Output from job"];

  return (
    <>
      <section>
        <div className="flex justify-between items-center mb-3 flex-wrap gap-3">
          <h2 className="text-xl font-bold text-gray-900">
            Linked input files <span className="text-gray-500 font-medium">({files.length})</span>
          </h2>

          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => setShowModal(true)}
              className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
            >
              Update
            </button>

            <button
              onClick={handleDownloadZip}
              disabled={downloading || files.length === 0}
              className="flex items-center gap-2 px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded"
            >
              {downloading ? "Downloading..." : "Download as zip"}
            </button>
          </div>
        </div>

        <div className="w-full bg-white shadow rounded-lg overflow-hidden">
          <div className="max-h-[300px] overflow-y-auto overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                  {HEADERS.map((h) => (
                    <th key={h} className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {files.length === 0 ? (
                  <tr>
                    <td colSpan={HEADERS.length} className="py-10 text-center text-gray-400 text-xs">
                      No input files linked.
                    </td>
                  </tr>
                ) : (
                  files.map((file, index) => {
                    const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                    return (
                      <tr key={file.id || index} className={`${rowBg} hover:bg-blue-50 transition-colors`}>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-700">
                          {file.file_code || `#${index + 1}`}
                        </td>
                        <td className="px-3 py-2 text-xs break-words">
                          <button
                            onClick={() => handleDownloadSingle(file)}
                            className="text-blue-600 hover:text-blue-800 underline font-medium"
                          >
                            {getFileName(file)}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-700">
                          {getFileSize(file)}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-700">
                          {file.category || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs break-words text-gray-600">
                          {file.note || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                          {formatDateTime(file.uploaded_at)}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-700">
                          {file.uploader?.details
                            ? `${file.uploader.details.first_name} ${file.uploader.details.last_name}`
                            : file.uploader?.email || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-700">
                          {file.output_from_job || "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Update Modal — pass `files` as existingFiles so modal knows what's already linked */}
      {showModal && (
        <UpdateLinkedFilesModal
          jobId={jobId}
          existingFiles={files}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            onRefresh();
          }}
        />
      )}
    </>
  );
};

export default LinkedInputFilesCard;